import { getTracker } from './tracker';

/**
 * Global error + browser-lifecycle telemetry.
 *
 * The React equivalent of the legacy Angular global `ErrorHandler`
 * (`RaptorUiErrorHandler` registered in `app.module.ts`), which forwarded every
 * uncaught error to AppInsights. Angular's zone-based handler catches both
 * synchronous throws AND unhandled promise rejections, so the faithful React
 * port registers both window listeners.
 *
 * Also ports the legacy `BROWSERCLOSE` event
 * (`app.component.ts` `@HostListener('window:beforeunload')` →
 * `logEvent(EventTypes.BROWSERCLOSE)`, fired only on app-container pages).
 */

let installed = false;

/** System routes that are NOT inside an authenticated MFE page. */
const NON_APP_ROUTES = new Set(['/login', '/frameredirect', '/error']);

export function installWindowTelemetry(): void {
  if (installed) return;
  installed = true;

  // Uncaught synchronous errors.
  window.addEventListener('error', (event) => {
    const err =
      event.error instanceof Error ? event.error : new Error(event.message || 'Unknown error');
    getTracker().error(err, {
      source: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
    });
  });

  // Unhandled promise rejections (Angular's ErrorHandler catches these too).
  window.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event.reason;
    const err =
      reason instanceof Error
        ? reason
        : new Error(typeof reason === 'string' ? reason : 'Unhandled promise rejection');
    getTracker().error(err, { source: 'unhandledrejection' });
  });

  // Browser-close event. Legacy fires it only on app-container pages; the React
  // equivalent is any authenticated page (everything except the system routes).
  window.addEventListener('beforeunload', () => {
    if (NON_APP_ROUTES.has(window.location.pathname)) return;
    getTracker().event('Browser Close');
  });
}
